import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import AdminCustomerList from '../components/AdminCustomerList';
import AdminCustomerDetail from '../components/AdminCustomerDetail';
import AdminPayoutCenter from '../components/AdminPayoutCenter';
import AdminPendingRegistrations from '../components/AdminPendingRegistrations';
import AdminBanking from '../components/AdminBanking';
import AdminAccountAuthorizations from '../components/AdminAccountAuthorizations';
import AdminAdvisors from '../components/AdminAdvisors';
import AdminBroadcast from '../components/AdminBroadcast';
import AdminKPIs from '../components/AdminKPIs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (amount) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingAuthCount, setPendingAuthCount] = useState(0);

  useEffect(() => {
    loadStats();
    loadPendingCount();
    loadPendingAuthCount();
  }, []);

  const loadPendingAuthCount = async () => {
    try {
      const res = await axios.get(`${API}/admin/account-authorizations?status_filter=pending_admin_review`);
      setPendingAuthCount((res.data || []).length);
    } catch (e) {
      console.error('Failed to load pending auth count', e);
    }
  };

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`);
      setStats({
        duePayouts: res.data?.payouts?.due_count || 0,
      });
    } catch (error) {
      console.error('Failed to load stats', error);
    }
  };

  const loadPendingCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-registrations`);
      setPendingCount(response.data.length);
    } catch (error) {
      console.error('Failed to load pending count', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background-subtle">
      <nav className="bg-primary text-primary-foreground shadow-md">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 sm:space-x-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_29d23b9a-c758-4d5b-a3ec-a84a3acd0754/artifacts/883asqj8_image.png" 
                  alt="Logo" 
                  className="h-6 sm:h-8" 
                  style={{filter: 'brightness(0) invert(1)'}} 
                />
                <h1 className="text-base sm:text-xl font-heading font-bold">Admin Portal</h1>
              </div>
              <button onClick={handleLogout} className="btn-secondary text-xs px-3 py-1.5 sm:hidden" data-testid="admin-logout-button-mobile">Abmelden</button>
            </div>
            <div className="flex items-center justify-between sm:justify-end space-x-3 sm:space-x-6">
              <div className="flex items-center space-x-3 sm:space-x-6 text-xs sm:text-sm overflow-x-auto">
                <Link to="/admin" className="hover:text-accent transition whitespace-nowrap">Kunden</Link>
                <Link to="/admin/registrierungen" className="hover:text-accent transition whitespace-nowrap flex items-center">
                  Registrierungen
                  {pendingCount > 0 && (
                    <span className="ml-2 px-1.5 py-0.5 bg-green-500 text-white text-xs rounded-full">{pendingCount}</span>
                  )}
                </Link>
                <Link to="/admin/banking" className="hover:text-accent transition whitespace-nowrap">Banking</Link>
                <Link to="/admin/payouts" className="hover:text-accent transition whitespace-nowrap flex items-center">
                  Auszahlungen
                  {stats?.duePayouts > 0 && (
                    <span className="ml-2 px-1.5 py-0.5 bg-warning text-white text-xs rounded-full">{stats.duePayouts}</span>
                  )}
                </Link>
                <Link to="/admin/vollmachten" className="hover:text-accent transition whitespace-nowrap flex items-center" data-testid="admin-nav-authorizations">
                  Vollmachten
                  {pendingAuthCount > 0 && (
                    <span className="ml-2 px-1.5 py-0.5 bg-blue-500 text-white text-xs rounded-full" data-testid="admin-auth-pending-badge">{pendingAuthCount}</span>
                  )}
                </Link>
                <Link to="/admin/berater" className="hover:text-accent transition whitespace-nowrap" data-testid="admin-nav-advisors">Berater</Link>
                <Link to="/admin/broadcast" className="hover:text-accent transition whitespace-nowrap" data-testid="admin-nav-broadcast">Bulk E-Mail</Link>
              </div>
              <div className="hidden sm:flex items-center space-x-4">
                <span className="text-xs sm:text-sm truncate max-w-[150px]">{user?.email}</span>
                {!(user?.twofa_enabled || user?.twofa_push_enabled) ? (
                  <Link to="/setup-2fa" className="text-xs bg-warning text-white px-3 py-1.5 rounded hover:bg-warning/90 transition" data-testid="setup-2fa-link">
                    2FA einrichten
                  </Link>
                ) : (
                  <Link
                    to="/setup-2fa"
                    className={`text-xs transition ${
                      (user?.recovery_codes_remaining ?? 99) <= 3
                        ? 'bg-red-100 text-red-800 px-3 py-1.5 rounded hover:bg-red-200 font-medium'
                        : 'text-muted-foreground hover:text-primary'
                    }`}
                    data-testid="security-link"
                  >
                    Sicherheit
                    {(user?.recovery_codes_remaining ?? 99) <= 3 && (
                      <span className="ml-1" data-testid="security-link-low-codes">
                        ({user?.recovery_codes_remaining ?? 0} Codes)
                      </span>
                    )}
                  </Link>
                )}
                <button onClick={handleLogout} className="btn-secondary" data-testid="admin-logout-button">Abmelden</button>
              </div>
            </div>
          </div>

          {/* Mobile-only: 2FA link below header so it doesn't get cropped in portrait */}
          {!(user?.twofa_enabled || user?.twofa_push_enabled) ? (
            <Link
              to="/setup-2fa"
              className="sm:hidden inline-flex items-center gap-2 text-xs bg-warning text-white px-3 py-1.5 rounded hover:bg-warning/90 transition mt-3"
              data-testid="setup-2fa-link-mobile"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              2FA jetzt einrichten
            </Link>
          ) : (
            <Link
              to="/setup-2fa"
              className={`sm:hidden inline-flex items-center gap-2 text-xs transition mt-3 ${
                (user?.recovery_codes_remaining ?? 99) <= 3
                  ? 'bg-red-100 text-red-800 px-3 py-1.5 rounded hover:bg-red-200 font-medium'
                  : 'text-muted-foreground hover:text-primary'
              }`}
              data-testid="security-link-mobile"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Sicherheit
              {(user?.recovery_codes_remaining ?? 99) <= 3 && (
                <span className="ml-1">({user?.recovery_codes_remaining ?? 0} Codes)</span>
              )}
            </Link>
          )}
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-8" data-testid="admin-dashboard">
        <Routes>
          <Route path="/" element={<><AdminKPIs /><AdminCustomerList /></>} />
          <Route path="/customers/:customerId" element={<AdminCustomerDetail />} />
          <Route path="/registrierungen" element={<AdminPendingRegistrations />} />
          <Route path="/banking" element={<AdminBanking />} />
          <Route path="/payouts" element={<AdminPayoutCenter />} />
          <Route path="/vollmachten" element={<AdminAccountAuthorizations />} />
          <Route path="/berater" element={<AdminAdvisors />} />
          <Route path="/broadcast" element={<AdminBroadcast />} />
        </Routes>
      </div>
    </div>
  );
};

export default AdminDashboard;
